import { Amplify } from "aws-amplify"

let isAmplifyConfigured = false
let currentTenantId: string | null = null

/**
 * Check if Amplify is actually configured by inspecting the singleton
 * This is needed because React context state can propagate before
 * Amplify.configure() completes, and also handles cases where
 * multiple module instances exist in the bundle
 */
export function isAmplifyReady(): boolean {
  try {
    const config = Amplify.getConfig()
    const hasUserPool = !!(config?.Auth?.Cognito?.userPoolId)
    console.log('🔍 [isAmplifyReady] Check:', {
      hasUserPool,
      userPoolId: config?.Auth?.Cognito?.userPoolId?.substring(0, 10) + '...',
      isAmplifyConfigured
    })
    return hasUserPool
  } catch (error) {
    console.log('🔍 [isAmplifyReady] Error:', error)
    return false
  }
}

/**
 * Bootstrap configuration from tenant backend
 */
interface BootstrapConfig {
  tenantId: string
  displayName: string
  amplifyConfig: {
    Auth: {
      Cognito: {
        userPoolId: string
        userPoolClientId: string
        identityPoolId?: string
        region: string
        loginWith: {
          username: boolean
          email: boolean
          phone_number?: boolean
          oauth?: {
            domain: string
            scopes: string[]
            redirectSignIn: string[]
            redirectSignOut: string[]
            responseType: string
            providers: string[] | Array<{ custom: string }>
          }
        }
      }
    }
    API?: {
      REST: {
        [moduleName: string]: {
          endpoint: string
          region: string
        }
      }
    }
  }
  theme: {
    logoUrl: string | null
    primaryColor: string
    secondaryColor: string
    customCss: string | null
  }
  userPoolConfig?: {
    signInIdentifiers: {
      username: boolean
      email: boolean
      phone: boolean
      preferredUsername: boolean
    }
    requiredAttributes: Array<{
      name: string
      type: string
      constraints: {
        minLength?: number
        maxLength?: number
        minValue?: number
        maxValue?: number
      }
    }>
    autoVerifiedAttributes: string[]
    writeableAttributes: string[]
  }
}

/**
 * Configure Amplify with bootstrap configuration from tenant backend
 * Uses 100% of the backend response without modifications
 * @param bootstrapConfig - Bootstrap configuration from tenant backend
 * @throws Error if configuration is invalid
 */
export function configureAmplifyWithTenant(bootstrapConfig: BootstrapConfig) {
  if (!bootstrapConfig) {
    throw new Error("Bootstrap configuration is required. Cannot configure Amplify without bootstrap data.")
  }

  // Check if we need to reconfigure for a different tenant
  if (isAmplifyConfigured && currentTenantId === bootstrapConfig.tenantId) {
    return
  }

  try {
    const { amplifyConfig } = bootstrapConfig

    if (!amplifyConfig?.Auth?.Cognito) {
      throw new Error(`Invalid bootstrap configuration: Missing Auth.Cognito configuration for tenant "${bootstrapConfig.displayName}"`)
    }

    // Configuration is already in Amplify format - use it directly (100% from backend)
    console.log('🔧 [Amplify] Calling Amplify.configure with:', JSON.stringify(amplifyConfig, null, 2))
    Amplify.configure(amplifyConfig as Parameters<typeof Amplify.configure>[0])

    // Verify configuration took effect
    const verifyConfig = Amplify.getConfig()
    const verified = !!(verifyConfig?.Auth?.Cognito?.userPoolId)
    console.log('🔧 [Amplify] Configuration verified:', {
      verified,
      userPoolId: verifyConfig?.Auth?.Cognito?.userPoolId?.substring(0, 10) + '...'
    })

    if (!verified) {
      throw new Error('Amplify.configure() did not take effect - getConfig() returns empty')
    }

    isAmplifyConfigured = true
    currentTenantId = bootstrapConfig.tenantId

    console.log(`✅ Amplify configured for Monumenta: ${bootstrapConfig.displayName} (${bootstrapConfig.tenantId})`)
    
    // Log configured modules for debugging
    if (amplifyConfig.API?.REST) {
      const modules = Object.keys(amplifyConfig.API.REST)
      console.log(`📡 API modules configured: ${modules.join(', ')}`)
    }
    
  } catch (error) {
    console.error('❌ Error configuring Amplify:', error)
    throw error
  }
}

/**
 * Reset Amplify configuration (useful for tenant switching)
 */
export function resetAmplifyConfiguration() {
  isAmplifyConfigured = false
  currentTenantId = null
} 